import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  const [t] = useTranslation("global");
  const isRtl = i18next.language === "ar";

  const pages = useMemo(() => {
    const last = Math.max(1, Number(totalPages) || 1);
    const current = Math.min(Math.max(1, Number(currentPage) || 1), last);

    if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);

    const set = new Set([1, last]);
    for (let i = current - 2; i <= current + 2; i++) {
      if (i > 1 && i < last) set.add(i);
    }
    const sorted = Array.from(set).sort((a, b) => a - b);

    const withGaps = [];
    for (let i = 0; i < sorted.length; i++) {
      const value = sorted[i];
      const prev = sorted[i - 1];
      if (i > 0 && value - prev > 1) withGaps.push("…");
      withGaps.push(value);
    }
    return withGaps;
  }, [currentPage, totalPages]);

  if (!totalPages || totalPages <= 1) return null;

  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= totalPages;

  return (
    <div
      className={`w-[98%] mx-auto mt-2 pb-2 flex items-center justify-between ${className}`}
    >
      <div className="text-sm text-[#261759]">
        {t("pagination.page")} {currentPage} {t("pagination.from")} {totalPages}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={prevDisabled}
          className={`p-1 rounded-lg bg-gray-200 hover:bg-gray-300 ${
            prevDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isRtl ? <MdNavigateNext size={20} /> : <MdNavigateBefore size={20} />}
        </button>

        {pages.map((p, idx) =>
          p === "…" ? (
            <span key={`gap-${idx}`} className="px-2 text-gray-500">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`px-3 py-1 rounded ${
                p === currentPage
                  ? "bg-[#261759] text-white"
                  : "bg-slate-200 hover:bg-slate-300 text-[#261759]"
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={nextDisabled}
          className={`p-1 rounded-lg bg-gray-200 hover:bg-gray-300 ${
            nextDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isRtl ? <MdNavigateBefore size={20} /> : <MdNavigateNext size={20} />}
        </button>
      </div>
    </div>
  );
};

export default Pagination;
